import React, { useEffect, useState, useCallback, useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import i18n from 'i18n-js';
import { buildUrl, truncateKeywordSpaces, toQueryString } from 'utils';
import {
  Grid,
  Paper,
  Container,
  InputAdornment,
  Button,
  Box,
  Tooltip,
  IconButton,
} from '@material-ui/core';
import { partnersActions } from 'state/modules/partners';
import { useStyles } from './PartnersList.styled';
import { FETCH_STATE } from 'constants';
import { useHistory } from 'react-router-dom';
import { useQuery } from 'hooks';
import { Search as SearchIcon } from '@material-ui/icons';
import { SearchField } from 'components/FormFields';
import { debounce } from 'lodash';
import EditIcon from '@material-ui/icons/Edit';
import Layout from 'components/Layout';
import Title from 'components/Title';
import {
  InviteListModal,
  InvitePartnerModal,
  UpdatePartnerStatusModal,
  InviteFFBPartnerModal,
} from 'components/Modals';
import { Table } from 'components/UI';
import { UserContext } from 'context';
import { PartnerStatusSelect, OrganizationTypeSelect } from 'components/DataFields';
import { removeEmpty, getValidPartnershipStates } from 'utils';

const ActionsMenu = ({ partner, orgType, handleUpdate, sharingType }) => {
  const { disabled } = getValidPartnershipStates(
    partner?.relationship?.status,
    orgType,
    sharingType
  );

  return disabled ? (
    i18n.t('common.n/a')
  ) : (
    <Box display="flex" justifyContent="center">
      <Tooltip title={i18n.t('partners.update_status')}>
        <IconButton
          aria-label="archive"
          size="medium"
          onClick={() => handleUpdate(partner)}
          disabled={disabled}
          data-cy="update-partner-btn"
        >
          <EditIcon fontSize="inherit" />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

const PartnersList = () => {
  const urlParams = useQuery();
  const [showInviteListModal, setShowInviteListModal] = useState(false);
  const [showInvitePartnerModal, setShowInvitePartnerModal] = useState(false);
  const [showInviteFFBPartnerModal, setShowInviteFFBPartnerModal] = useState(false);
  const [showUpdatePartnerStatusModal, setShowUpdatePartnerStatusModal] = useState(false);
  const [partner, setPartner] = useState(null);
  const [params, setParams] = useState({
    page: 1,
    limit: 10,
    ...urlParams,
  });

  const dispatch = useDispatch();
  const history = useHistory();
  const classes = useStyles();
  const { orgType } = useContext(UserContext);

  // We use multiple selectors to avoid table rerender / consider using reselect.
  const data = useSelector((state) => state.partners.partners);
  const pagination = useSelector((state) => state.partners.pagination);
  const fetchingPartners = useSelector((state) => state.partners.fetchingPartners);
  const deletePartner = useSelector((state) => state.partners.deletePartner);
  const updatePartnerState = useSelector((state) => state.partners.updatePartnerState);
  const sharingType = useSelector(
    (state) => state.config?.settings?.core?.partnership?.sharing_type
  );
  const allowInvite = useSelector(
    (state) => state.config?.settings?.core?.partnership?.allow_invite || false
  );

  const refetch =
    deletePartner === FETCH_STATE.DONE ||
    updatePartnerState === FETCH_STATE.DONE ||
    fetchingPartners === FETCH_STATE.UNSENT;
  const loading = fetchingPartners === FETCH_STATE.LOADING;

  const fetchPartners = useCallback(
    debounce((params = {}) => {
      dispatch(partnersActions.fetchPartners(params));
    }, 1000),
    [dispatch]
  );

  useEffect(() => {
    if (orgType === 'msOwnerOrg') {
      setParams({
        ...params,
        organization_type: sharingType === 'FFB' ? 'BUSINESS' : 'SERVICE',
      });
    }
  }, [sharingType, orgType]);

  // Fetch on load/ params change
  useEffect(() => {
    fetchPartners(params);
  }, [fetchPartners, params]);

  // Fetch when data is outdated
  useEffect(() => {
    if (refetch) {
      fetchPartners(params);
    }
  }, [fetchPartners, params, refetch]);

  const handleSort = ({ column, sortDirection }) => {
    const newParams = {
      ...params,
      sort_field: column.selector,
      sort_desc: sortDirection === 'desc' ? true : false,
    };
    updateParams(newParams);
  };

  const handleSearch = (name, value) => {
    const newParams = {
      ...params,
      page: 1,
      [name]: truncateKeywordSpaces(value),
    };
    updateParams(newParams);
  };

  const handlePageChange = (page) => {
    const newParams = { ...params, page };
    updateParams(newParams);
  };

  const handlePerRowsChange = async (limit) => {
    const newParams = { ...params, page: 1, limit };
    updateParams(newParams);
  };

  /**
   * Handle row click
   * @param {object} row Data of the row selected
   */
  const handleRowClick = (row) => {
    history.push(buildUrl(`/partners/${row?.id}`));
  };

  const updateParams = (newParams) => {
    const cleanParam = removeEmpty(newParams);
    const qs = toQueryString(cleanParam);
    history.replace(buildUrl(`/partners${qs}`));
    setParams(cleanParam);
  };

  const handleUpdate = (row) => {
    if (row?.id) {
      setPartner(row);
      setShowUpdatePartnerStatusModal(true);
    }
  };

  const columns = [
    {
      name: i18n.t('common.id'),
      selector: 'id',
      grow: 0,
      minWidth: '9rem',
      sortable: true,
    },
    {
      name: i18n.t('partners.partner_name'),
      selector: 'name',
      format: (row) => row?.name || '--',
      grow: 2,
      sortable: true,
    },
    {
      name: i18n.t('common.status'),
      selector: 'status',
      format: (row) =>
        i18n.t(`partners.statuses.${row?.relationship?.status}`, {
          defaultValue: row?.relationship?.status || '--',
        }),
      grow: 2,
    },
    {
      name: i18n.t('common.type_of'),
      selector: 'organization_type',
      format: (row) =>
        i18n.t(`partners.type.${row.organization_type}`, {
          defaultValue: row.organization_type || '--',
        }),
      grow: 2,
    },
    {
      name: i18n.t('common.actions'),
      center: true,
      grow: 0,
      minWidth: '9rem',
      cell: (row) => (
        <ActionsMenu
          partner={row}
          orgType={orgType}
          handleUpdate={handleUpdate}
          sharingType={sharingType}
        />
      ),
    },
  ];

  return (
    <Layout title={i18n.t('partners.title')}>
      <InviteListModal
        open={showInviteListModal}
        handleCancel={() => setShowInviteListModal(false)}
      />
      <InvitePartnerModal
        open={showInvitePartnerModal}
        handleCancel={() => setShowInvitePartnerModal(false)}
      />
      <UpdatePartnerStatusModal
        open={showUpdatePartnerStatusModal}
        handleCancel={() => {
          setPartner(null);
          setShowUpdatePartnerStatusModal(false);
        }}
        partner={partner}
      />
      <InviteFFBPartnerModal
        open={showInviteFFBPartnerModal}
        handleCancel={() => setShowInviteFFBPartnerModal(false)}
      />
      <Container className={classes.container}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Grid container spacing={2} direction="row" alignItems="center">
              <Grid item style={{ marginRight: 'auto' }}>
                <Title title={i18n.t('partners.title')} />
              </Grid>
              {(orgType === 'msServiceOrg' && sharingType !== 'FFB' && allowInvite) ||
              (orgType === 'msOwnerOrg' && sharingType === 'FFB' && allowInvite) ? (
                <Grid item>
                  <Button
                    onClick={() => {
                      setShowInviteListModal(true);
                    }}
                    style={{ textTransform: 'capitalize' }}
                    variant="outlined"
                    color="primary"
                    data-cy="invite-list-button"
                  >
                    {i18n.t('partners.invites')}
                  </Button>
                </Grid>
              ) : null}
              {(sharingType === 'FFB' && orgType === 'msOwnerOrg' && allowInvite) ||
              (orgType === 'msServiceOrg' && sharingType === 'FAAS' && allowInvite) ? (
                <Grid item>
                  <Button
                    onClick={() => {
                      sharingType === 'FFB'
                        ? setShowInviteFFBPartnerModal(true)
                        : setShowInvitePartnerModal(true);
                    }}
                    style={{ textTransform: 'capitalize' }}
                    variant="contained"
                    color="primary"
                    data-cy="invite-partner-button"
                  >
                    {i18n.t('partners.invite_partner')}
                  </Button>
                </Grid>
              ) : null}
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Grid
              container
              spacing={2}
              direction="row"
              alignItems="flex-end"
              style={{ marginBottom: '25px' }}
            >
              <Grid item xs={3}>
                <SearchField
                  margin="none"
                  label={i18n.t('partners.search')}
                  placeholder={i18n.t('partners.search_placeholder')}
                  field={{ onChange: handleSearch, name: 'keyword', value: params.keyword }}
                  inputProps={{
                    startAdornment: (
                      <InputAdornment
                        classes={{
                          root: classes.searchIcon,
                        }}
                        position="start"
                      >
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                  data-cy="search-partners-input"
                />
              </Grid>
              {/* <Feature name="partnership" is="enabled">
                {sharingType === 'FFB' && orgType === 'msOwnerOrg' && (
                  <Grid item xs={3}>
                    <OrganizationTypeSelect
                      form={{
                        setFieldValue: handleSearch,
                      }}
                      field={{
                        name: 'organization_type',
                        value: params.organization_type,
                      }}
                      label={i18n.t('common.by_type')}
                    />
                  </Grid>
                )}
              </Feature> */}
              <Grid item xs={3}>
                <PartnerStatusSelect
                  form={{
                    setFieldValue: handleSearch,
                  }}
                  field={{
                    name: 'status',
                    value: params.status,
                  }}
                  label={i18n.t('common.status')}
                  sharingType={sharingType}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Paper elevation={2} className={classes.paper}>
              <Table
                data={data}
                columns={columns}
                onSort={handleSort}
                loading={loading}
                handleRowClicked={handleRowClick}
                handlePageChange={handlePageChange}
                totalRows={pagination?.totalRows || 0}
                handlePerRowsChange={handlePerRowsChange}
                page={params?.page}
                limit={params?.limit}
                defaultSortField={params?.sort_field}
                defaultSortAsc={!params?.sort_desc}
                data-cy="partner-list"
              />
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Layout>
  );
};

export { PartnersList };
